import axiosInstance from "../../scenes/utils/axiosInstance";
import { API_ENDPOINTS } from "../../config/api";
import { withExportLang } from "../../utils/getExportLangParam";
import { getDoctorSessionCacheKey } from "../../utils/authSession";

const fileCache = new Map();
const inFlightFileRequests = new Map();
const singleFileCache = new Map();
const inFlightSingleFileRequests = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000;
/** clearFileCache sonrası tamamlanan eski isteklerin önbelleği yeniden doldurmasını engeller */
let fileCacheGeneration = 0;

export function clearFileCache() {
  fileCacheGeneration += 1;
  fileCache.clear();
  inFlightFileRequests.clear();
  singleFileCache.clear();
  inFlightSingleFileRequests.clear();
}

export default class FileService {
 createFile = async (data) => {
    try {
        const response = await axiosInstance.post(API_ENDPOINTS.FILES.CREATE, data);
        clearFileCache();
        return response.data;
    }
    catch(error) {
        console.log("Bir hata oluştu.createFile");
        throw error;
    }
 }
 getAllFiles = async (options = {}) => {
    try {
        const _uid = getDoctorSessionCacheKey();
        const cacheKey = JSON.stringify({ _uid, ...options });
        const now = Date.now();
        const cached = fileCache.get(cacheKey);
        if (cached && now - cached.timestamp < CACHE_TTL_MS) {
            return cached.data;
        }
        if (inFlightFileRequests.has(cacheKey)) {
            return await inFlightFileRequests.get(cacheKey);
        }

        const fetchPromise = (async () => {
            const generationAtStart = fileCacheGeneration;
            try {
                const response = await axiosInstance.get(API_ENDPOINTS.FILES.ALL, options);
                let result;
                if (options.withMeta) {
                    result = { data: response.data, headers: response.headers };
                } else {
                    result = response.data;
                }
                if (generationAtStart === fileCacheGeneration) {
                    fileCache.set(cacheKey, { data: result, timestamp: Date.now() });
                }
                return result;
            } finally {
                inFlightFileRequests.delete(cacheKey);
            }
        })();

        inFlightFileRequests.set(cacheKey, fetchPromise);
        return await fetchPromise;
    } catch(error) {
        console.log("Bir hata oluştu.getAllFiles");
    }
 };
 getCachedFiles = (options = {}) => {
    const _uid = getDoctorSessionCacheKey();
    const cacheKey = JSON.stringify({ _uid, ...options });
    const cached = fileCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
    }
    return null;
 };
 getSuggestions = async (q, limit = 20) => {
    const response = await axiosInstance.get(API_ENDPOINTS.FILES.SUGGESTIONS, { params: { q, limit } });
    return response.data;
 }
 exportFilesExcel = async (filters = {}) => {
    const response = await axiosInstance.get(API_ENDPOINTS.FILES.EXPORT_EXCEL, {
        params: withExportLang(filters),
        responseType: "blob",
    });
    return response;
 }
 exportFileFormsExcel = async (fileId, filters = {}) => {
    return axiosInstance.get(API_ENDPOINTS.FILES.EXPORT_FORMS_EXCEL(fileId), {
        params: withExportLang(filters),
        responseType: "blob",
    });
 }
 exportFilePatientsExcel = async (fileId, filters = {}) => {
    return axiosInstance.get(API_ENDPOINTS.FILES.EXPORT_PATIENTS_EXCEL(fileId), {
        params: withExportLang(filters),
        responseType: "blob",
    });
 }
  getCachedFileInfo = (fileId, options = {}) => {
    const _uid = getDoctorSessionCacheKey();
    const cacheKey = `${_uid || 'anon'}_${fileId}_${JSON.stringify(options.params || {})}`;
    const cached = singleFileCache.get(cacheKey);
    const now = Date.now();
    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    return null;
  };

  getFileInfo = async (fileID, options = {}) => {
    try {
      const _uid = getDoctorSessionCacheKey();
      const cacheKey = `${_uid || 'anon'}_${fileID}_${JSON.stringify(options.params || {})}`;
      const now = Date.now();
      const cached = singleFileCache.get(cacheKey);
      if (!options.forceRefresh && cached && now - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
      }
      if (inFlightSingleFileRequests.has(cacheKey)) {
        return await inFlightSingleFileRequests.get(cacheKey);
      }

      const fetchPromise = (async () => {
        const generationAtStart = fileCacheGeneration;
        try {
          const response = await axiosInstance.get(API_ENDPOINTS.FILES.BY_ID(fileID), options);
          if (generationAtStart === fileCacheGeneration) {
            singleFileCache.set(cacheKey, { data: response.data, timestamp: Date.now() });
          }
          return response.data;
        } finally {
          inFlightSingleFileRequests.delete(cacheKey);
        }
      })();

      inFlightSingleFileRequests.set(cacheKey, fetchPromise);
      return await fetchPromise;
    } catch (error) {
      if (
        error?.name === "CanceledError" ||
        error?.name === "AbortError" ||
        error?.code === "ERR_CANCELED"
      ) {
        throw error;
      }
      console.log("Bir sorun oluştu.getFileInfo", error);
      throw error;
    }
  };
  deleteFile = async (fileId) => {
    try {
        const response = await axiosInstance.delete(API_ENDPOINTS.FILES.DELETE(fileId));
        clearFileCache();
        return response.data;
    } catch (error) {
        console.error("Bir sorun oluştu:", error.response?.data || error.message);
        throw error;
    }
  }

  copyFile = async (fileId, newFileName) => {
    try {
        const response = await axiosInstance.post(API_ENDPOINTS.FILES.COPY(fileId), { newFileName });
        clearFileCache();
        return response.data;
    } catch (error) {
        console.error("Copy file error:", error.response?.data || error.message);
        throw error;
    }
  }

  updateFile = async (fileId, updateData) => {
    try {
        const response = await axiosInstance.put(API_ENDPOINTS.FILES.UPDATE(fileId), updateData);
        clearFileCache();
        return response.data;
    } catch (error) {
        console.error("Update file error:", error.response?.data || error.message);
        throw error;
    }
  }
}
